import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    CircularProgress,
    Alert,
    useTheme,
    alpha,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Security as SecurityIcon,
    Mail as MailIcon,
    Person as PersonIcon,
} from '@mui/icons-material';
import { apiService } from '../services/api.service';
import { authService } from '../services/auth.service';
import { AdminUser } from '../types';
import Layout from '../components/Layout';

const Admins: React.FC = () => {
    const theme = useTheme();
    const [admins, setAdmins] = useState<AdminUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Modal State
    const [open, setOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        role: 'admin' as AdminUser['role'],
    });

    useEffect(() => {
        loadAdmins();
    }, []);

    const loadAdmins = async () => {
        try {
            setLoading(true);
            const data = await apiService.getAdmins();
            setAdmins(data);
        } catch (err) {
            console.error('Failed to load admins:', err);
            setError('Failed to load admin users.');
        } finally {
            setLoading(false);
        }
    };

    const handleOpen = () => {
        setFormData({ name: '', email: '', password: '', role: 'admin' });
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setError(null);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setSubmitting(true);
            setError(null);
            await authService.createAdmin(formData);
            setSuccess('Admin user created successfully.');
            handleClose();
            loadAdmins();
            setTimeout(() => setSuccess(null), 3000);
        } catch (err: any) {
            console.error('Failed to create admin:', err);
            setError(err.message || 'Failed to create admin user.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id: string, email: string) => {
        if (window.confirm(`Are you sure you want to delete admin ${email}?`)) {
            try {
                await apiService.deleteAdmin(id);
                setSuccess('Admin user deleted.');
                loadAdmins();
                setTimeout(() => setSuccess(null), 3000);
            } catch (err) {
                console.error('Failed to delete admin:', err);
                setError('Failed to delete admin user.');
            }
        }
    };

    const getRoleColor = (role: string) => {
        switch (role) {
            case 'super_admin': return 'error';
            case 'admin': return 'primary';
            case 'support': return 'info';
            default: return 'default';
        }
    };

    if (loading && admins.length === 0) {
        return (
            <Layout>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            </Layout>
        );
    }

    return (
        <Layout>
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                        Admin Management
                    </Typography>
                    <Typography color="text.secondary" variant="body2">
                        Manage administrative access and roles for the portal.
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleOpen}
                    sx={{ borderRadius: 2, px: 3, py: 1 }}
                >
                    Create New Admin
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>{success}</Alert>}

            <Paper sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: theme.shadows[2] }}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {admins.map((admin) => (
                                <TableRow key={admin.id} hover>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <PersonIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{admin.name}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <MailIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                                            <Typography variant="body2">{admin.email}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            icon={<SecurityIcon sx={{ fontSize: '16px !important' }} />}
                                            label={admin.role.replace('_', ' ').toUpperCase()}
                                            size="small"
                                            color={getRoleColor(admin.role) as any}
                                            sx={{ fontWeight: 600, fontSize: '0.65rem' }}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton
                                            color="error"
                                            onClick={() => handleDelete(admin.id, admin.email)}
                                            disabled={admin.email === authService.getCurrentUser()?.email}
                                            size="small"
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {admins.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                        <Typography color="text.secondary">No admins found.</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <form onSubmit={handleSubmit}>
                    <DialogTitle sx={{ fontWeight: 700 }}>Create New Admin</DialogTitle>
                    <DialogContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                            <TextField
                                name="name"
                                label="Full Name"
                                fullWidth
                                required
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="Enter admin name"
                            />
                            <TextField
                                name="email"
                                label="Email Address"
                                type="email"
                                fullWidth
                                required
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="admin@teachora.com"
                            />
                            <TextField
                                name="password"
                                label="Initial Password"
                                type="password"
                                fullWidth
                                required
                                value={formData.password}
                                onChange={handleChange}
                                placeholder="Minimum 6 characters"
                                inputProps={{ minLength: 6 }}
                            />
                            <TextField
                                name="role"
                                label="Role"
                                select
                                fullWidth
                                required
                                value={formData.role}
                                onChange={handleChange}
                            >
                                <MenuItem value="admin">Admin</MenuItem>
                                <MenuItem value="support">Support</MenuItem>
                                <MenuItem value="super_admin">Super Admin</MenuItem>
                            </TextField>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: 3 }}>
                        <Button onClick={handleClose} color="inherit">Cancel</Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={submitting}
                            startIcon={submitting && <CircularProgress size={20} color="inherit" />}
                        >
                            {submitting ? 'Creating...' : 'Create Admin'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </Layout>
    );
};

export default Admins;
